import { useNavigate } from 'react-router-dom'
import { AlarmClock, X } from 'lucide-react'
import { useTaskReminderCycle } from '@/modules/tasks/hooks/useTaskReminderCycle'
import {
  formatReminderCount,
  tasksReminderHref,
} from '@/modules/tasks/utils/task-reminder'
import { usePermission } from '@/hooks/useAuth'
import { usePrefersReducedMotion } from '@/hooks/useMediaQuery'
import type { ModuleId } from '@/constants/modules'
import { cn } from '@/utils/cn'

interface TaskReminderChipProps {
  /** Hidden inside Tasks — you are already looking at the list there. */
  currentModuleId?: ModuleId
}

/**
 * Desktop header pill — always visible when Overdue / To do counts exist.
 * Soft CSS nudge while shown (not gated on the session burst). X dismisses
 * the current status only. Hidden on Tasks (R1A).
 */
export function TaskReminderChip({ currentModuleId }: TaskReminderChipProps) {
  const navigate = useNavigate()
  const reducedMotion = usePrefersReducedMotion()
  const inTasksModule = currentModuleId === 'tasks'
  const canViewTasks = usePermission('tasks.view')
  // Keep the cycle enabled across module changes — only hide UI inside Tasks.
  const { current, visible, dismiss } = useTaskReminderCycle(canViewTasks, {
    oncePerSession: true,
  })

  if (!canViewTasks || inTasksModule || !visible || !current) return null

  const isOverdue = current.kind === 'overdue'

  return (
    <div
      className={cn(
        'relative flex h-8 max-w-[min(100%,14rem)] shrink-0 items-center gap-0.5 rounded-full border pl-2 sm:h-9',
        isOverdue
          ? 'border-destructive/30 bg-destructive/[0.12] text-destructive shadow-[inset_0_1px_0_0_rgb(255_255_255_/_0.35)]'
          : 'border-primary/30 bg-primary/[0.12] text-primary shadow-[inset_0_1px_0_0_rgb(255_255_255_/_0.45)]',
        // Soft transform nudge whenever the chip is on screen (Follow-up amplitude).
        !reducedMotion && 'animate-pmms-attention-nudge',
      )}
    >
      <button
        type="button"
        onClick={() => navigate(tasksReminderHref(current.kind))}
        aria-label={`${current.label}, ${current.count} tasks`}
        className={cn(
          'flex min-w-0 items-center gap-1.5 rounded-full py-1 pr-1 transition-colors duration-150',
          isOverdue ? 'hover:bg-destructive/10' : 'hover:bg-primary/10',
        )}
      >
        <AlarmClock className="h-3.5 w-3.5 shrink-0" aria-hidden />
        <span className="max-w-[6.5rem] truncate whitespace-nowrap text-[11px] font-medium sm:max-w-[8.5rem] sm:text-xs">
          {current.label}
        </span>
        <span
          className={cn(
            'flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[8px] font-bold leading-none tabular-nums text-white ring-2 ring-card',
            isOverdue ? 'bg-destructive' : 'bg-primary',
          )}
        >
          {formatReminderCount(current.count)}
        </span>
      </button>
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation()
          dismiss()
        }}
        className={cn(
          'mr-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full transition-colors',
          isOverdue ? 'hover:bg-destructive/15' : 'hover:bg-primary/15',
        )}
        aria-label="Dismiss this reminder"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  )
}
